import { useMemo, type CSSProperties } from "react";
import type { Constellation } from "@/api/types";
import {
  constellationBounds,
  constellationEdges,
  fitToAspect,
  magnitudes,
  orient,
  padBounds,
  photoTransform,
  spotPoints,
  type Bounds,
} from "@/lib/constellation";

/** Every plate is drawn in a 1000-unit-wide coordinate system, whatever its source size. */
const W = 1000;
const DOT_SIZES = [5.2, 4.4, 3.6, 3, 2.5];

export interface PlateProps {
  /** Null draws the spots as a chart on the graticule (spot-only records). */
  imageUrl: string | null;
  constellation: Constellation;
  aspect: number;
  /** Accessible description of the figure. */
  label: string;
  /** Region of the image to show, in image pixels; defaults to the shark, padded. */
  crop?: Bounds;
  /** Matched spots: spot index → pair label. */
  marks?: ReadonlyMap<number, number>;
  active?: number | null;
  onActive?: (label: number | null) => void;
  /** Small thumbnails: bigger dots, no ruler. */
  compact?: boolean;
  /** A Groth triangle to annotate, by spot index. */
  triangle?: readonly [number, number, number] | null;
  /** Draw the spots' constellation lines (chart mode). */
  edges?: boolean;
  /** Draw the segmented body outline. */
  outline?: boolean;
  scanning?: boolean;
}

/** A plate shaped like the (oriented) shark, clamped to [lo, hi], so photos are not letterboxed. */
export function sharkAspect(c: Constellation, lo = 1, hi = 2.4): number {
  const shape = padBounds(constellationBounds(orient(c)), 0.05);
  return Math.min(Math.max(shape.width / shape.height, lo), hi);
}

export function plateCrop(c: Constellation, aspect: number, padding = 0.05): Bounds {
  const o = orient(c);
  const frame = { x: 0, y: 0, width: o.width, height: o.height };
  return fitToAspect(padBounds(constellationBounds(o), padding), aspect, frame);
}

function Ruler({ height, onPhoto }: { height: number; onPhoto: boolean }) {
  const stroke = onPhoto ? "rgb(242 242 238 / 0.55)" : "var(--ink-3)";
  const ticks: React.ReactNode[] = [];
  for (let u = 0; u <= W; u += 25) {
    const len = u % 100 === 0 ? 12 : 6;
    ticks.push(<line key={`t${u}`} x1={u} y1={0} x2={u} y2={len} />);
    ticks.push(<line key={`b${u}`} x1={u} y1={height} x2={u} y2={height - len} />);
  }
  for (let v = 0; v <= height; v += 25) {
    const len = v % 100 === 0 ? 12 : 6;
    ticks.push(<line key={`l${v}`} x1={0} y1={v} x2={len} y2={v} />);
    ticks.push(<line key={`r${v}`} x1={W} y1={v} x2={W - len} y2={v} />);
  }
  return (
    <g stroke={stroke} strokeWidth={1.2} aria-hidden="true">
      {ticks}
    </g>
  );
}

function Graticule({ height }: { height: number }) {
  const lines: React.ReactNode[] = [];
  for (let u = 50; u < W; u += 50) {
    lines.push(<line key={`v${u}`} x1={u} y1={0} x2={u} y2={height} />);
  }
  for (let v = 50; v < height; v += 50) {
    lines.push(<line key={`h${v}`} x1={0} y1={v} x2={W} y2={v} />);
  }
  return (
    <g stroke="var(--grat)" strokeWidth={1} aria-hidden="true">
      <rect width={W} height={height} fill="var(--panel)" stroke="none" />
      {lines}
    </g>
  );
}

function Crosshair({
  x,
  y,
  height,
  label,
}: {
  x: number;
  y: number;
  height: number;
  label: number;
}) {
  const boxW = 16 + String(label).length * 11;
  const flip = x + 20 + boxW > W;
  const bx = flip ? x - 20 - boxW : x + 20;
  return (
    <g pointerEvents="none">
      <line x1={x} y1={0} x2={x} y2={height} stroke="var(--signal)" strokeWidth={1} opacity={0.7} />
      <line x1={0} y1={y} x2={W} y2={y} stroke="var(--signal)" strokeWidth={1} opacity={0.7} />
      <rect x={bx} y={y - 30} width={boxW} height={22} fill="var(--signal)" />
      <text
        x={bx + boxW / 2}
        y={y - 14}
        textAnchor="middle"
        fontFamily="var(--mono)"
        fontSize={15}
        fontWeight={600}
        fill="#111110"
      >
        {label}
      </text>
    </g>
  );
}

/**
 * A figure plate: a photo (or the graticule, for spot-only records) cropped to the shark, with
 * every detected spot plotted and matched spots ringed in the signal colour.
 */
export function Plate(props: PlateProps) {
  const { imageUrl, aspect, label, marks, active, onActive, compact } = props;
  // Every plate faces its view's way (head left on a left flank, back up), photo included.
  const c = useMemo(() => orient(props.constellation), [props.constellation]);
  const height = W / aspect;
  const crop = props.crop ?? plateCrop(c, aspect, imageUrl ? 0.05 : 0.12);
  const s = W / crop.width;
  const k = compact ? 1.5 : 1;
  const px = (x: number) => (x - crop.x) * s;
  const py = (y: number) => (y - crop.y) * s;
  const mags = useMemo(() => magnitudes(c.spots.map((spot) => spot[2])), [c]);
  const edges = useMemo(
    () => (props.edges ? constellationEdges(spotPoints(c)) : []),
    [c, props.edges],
  );
  const onPhoto = imageUrl !== null;
  const activeIndex = active == null || !marks ? -1 : findIndex(marks, active);

  return (
    <div className={`plate${onPhoto ? "" : " plate-chart"}`}>
      <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-label={label}>
        {onPhoto ? (
          <>
            <rect width={W} height={height} fill="var(--plate)" />
            <svg
              width={W}
              height={height}
              viewBox={`${crop.x} ${crop.y} ${crop.width} ${crop.height}`}
              preserveAspectRatio="none"
            >
              <image
                href={imageUrl}
                width={c.source.width}
                height={c.source.height}
                transform={c.turn ? photoTransform(c) : undefined}
                preserveAspectRatio="none"
              />
            </svg>
          </>
        ) : (
          <Graticule height={height} />
        )}
        {!compact && <Ruler height={height} onPhoto={onPhoto} />}
        {props.outline && c.body_polygon.length > 2 && (
          <polygon
            points={c.body_polygon.map(([x, y]) => `${px(x)},${py(y)}`).join(" ")}
            fill="color-mix(in srgb, var(--signal) 10%, transparent)"
            stroke="var(--signal)"
            strokeWidth={compact ? 5 : 2}
            strokeLinejoin="round"
          />
        )}
        {edges.length > 0 && (
          <g stroke="var(--ink-3)" strokeWidth={1.2} opacity={0.7}>
            {edges.map(([a, b]) => (
              <line
                key={`${a}-${b}`}
                x1={px(c.spots[a]![0])}
                y1={py(c.spots[a]![1])}
                x2={px(c.spots[b]![0])}
                y2={py(c.spots[b]![1])}
              />
            ))}
          </g>
        )}
        <g>
          {c.spots.map(([x, y], i) =>
            marks?.has(i) ? null : onPhoto && !marks ? (
              // No matching to show: every detected spot is the evidence, so ring them all.
              <circle
                key={i}
                cx={px(x)}
                cy={py(y)}
                r={Math.max(4.5, Math.min(c.spots[i]![2] * s * 1.3, 14))}
                className="spot-ring ring-in"
                strokeWidth={1.6}
                style={{ "--i": i / 3 } as CSSProperties}
              />
            ) : (
              <circle
                key={i}
                cx={px(x)}
                cy={py(y)}
                r={(onPhoto ? 2.6 : DOT_SIZES[(mags[i] ?? 3) - 1]!) * k}
                className={onPhoto ? "spot-dot" : "chart-dot"}
                opacity={marks ? 0.5 : onPhoto ? 0.85 : 0.9}
              />
            ),
          )}
        </g>
        {props.triangle && <TriangleMark c={c} tri={props.triangle} px={px} py={py} />}
        {marks && (
          <g>
            {[...marks].map(([i, pair]) => {
              const spot = c.spots[i];
              if (!spot) return null;
              const on = pair === active;
              return (
                <circle
                  key={i}
                  cx={px(spot[0])}
                  cy={py(spot[1])}
                  r={Math.max(compact ? 14 : 8, Math.min(spot[2] * s * 1.5, 22))}
                  className="spot-ring ring-in"
                  strokeWidth={on ? 3.5 : compact ? 4 : 1.8}
                  style={{ "--i": pair, pointerEvents: "all" } as CSSProperties}
                  onMouseEnter={onActive ? () => onActive(pair) : undefined}
                  onMouseLeave={onActive ? () => onActive(null) : undefined}
                />
              );
            })}
          </g>
        )}
        {activeIndex >= 0 && c.spots[activeIndex] && (
          <Crosshair
            x={px(c.spots[activeIndex][0])}
            y={py(c.spots[activeIndex][1])}
            height={height}
            label={active!}
          />
        )}
      </svg>
      {props.scanning && <div className="scan" aria-hidden="true" />}
    </div>
  );
}

/**
 * A thumbnail-sized constellation for spot-only specimens: the spots and their asterism lines,
 * scaled so they stay legible at card and table-row sizes where a full plate would vanish.
 * `dot` is the spot radius as a fraction of the glyph's width.
 */
export function SpotGlyph({
  constellation,
  label,
  dot = 0.014,
}: {
  constellation: Constellation;
  label: string;
  dot?: number;
}) {
  const c = useMemo(() => orient(constellation), [constellation]);
  const aspect = 4 / 3;
  const height = W / aspect;
  const crop = plateCrop(c, aspect, 0.14);
  const s = W / crop.width;
  const px = (x: number) => (x - crop.x) * s;
  const py = (y: number) => (y - crop.y) * s;
  const edges = useMemo(() => constellationEdges(spotPoints(c)), [c]);
  return (
    <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-label={label} className="glyph">
      <rect width={W} height={height} fill="var(--panel)" />
      <g stroke="var(--ink-3)" strokeWidth={W * dot * 0.3} opacity={0.6}>
        {edges.map(([a, b]) => (
          <line
            key={`${a}-${b}`}
            x1={px(c.spots[a]![0])}
            y1={py(c.spots[a]![1])}
            x2={px(c.spots[b]![0])}
            y2={py(c.spots[b]![1])}
          />
        ))}
      </g>
      <g fill="var(--ink)">
        {c.spots.map(([x, y], i) => (
          <circle key={i} cx={px(x)} cy={py(y)} r={W * dot} />
        ))}
      </g>
    </svg>
  );
}

function findIndex(marks: ReadonlyMap<number, number>, label: number): number {
  for (const [i, pair] of marks) if (pair === label) return i;
  return -1;
}

function TriangleMark({
  c,
  tri,
  px,
  py,
}: {
  c: Constellation;
  tri: readonly [number, number, number];
  px: (x: number) => number;
  py: (y: number) => number;
}) {
  const pts = tri.map((i) => ({ x: px(c.spots[i]![0]), y: py(c.spots[i]![1]) }));
  const cx = (pts[0]!.x + pts[1]!.x + pts[2]!.x) / 3;
  const cy = (pts[0]!.y + pts[1]!.y + pts[2]!.y) / 3;
  return (
    <g>
      <polygon
        points={pts.map((p) => `${p.x},${p.y}`).join(" ")}
        fill="color-mix(in srgb, var(--signal) 14%, transparent)"
        stroke="var(--signal)"
        strokeWidth={2}
      />
      {pts.map((p, n) => {
        const dx = p.x - cx;
        const dy = p.y - cy;
        const d = Math.hypot(dx, dy) || 1;
        return (
          <g key={n}>
            <circle cx={p.x} cy={p.y} r={9} fill="none" stroke="var(--signal)" strokeWidth={2} />
            <text
              x={p.x + (dx / d) * 26}
              y={p.y + (dy / d) * 26 + 6}
              textAnchor="middle"
              fontFamily="var(--mono)"
              fontSize={17}
              fontWeight={600}
              fill="var(--signal-ink)"
            >
              {"ABC"[n]}
            </text>
          </g>
        );
      })}
    </g>
  );
}
